'use client';
import { ReactNode } from 'react';
import Link from 'next/link';
import AssetImage from '@/components/ui/AssetImage';
import { useRouter } from 'next/navigation';
import { useOAuthFlow, useOAuthHref } from '@/contexts/OAuthFlowContext';
import { useTranslations } from 'next-intl';
import { GEAR, GEAR_CAPTURE_MAP, MODEL_SIZE, FORMAT, type ArchitectureFilter } from '@/utils/constants';
import { GearIcon, ICON_BG_COLOR_MAP } from '@/components/icons/Gear';
import { preferredModelsCount, visibleModelsCount } from '@/utils/architecture';
import { FolderClosed } from 'lucide-react';
import { MessageCircle } from 'lucide-react';
import { DownloadIcon } from 'lucide-react';
import { toTonePathname } from '@/utils/helpers';
import { TimeAgo } from '@/components/ui/TimeAgo/TimeAgo';
import { formatCount } from '@/utils/format-count';
import { ToneCountWithUser } from '@/utils/helpers';
import ToneCardUser from './ToneCardUser';
import { Varified } from '@/components/icons/Varified';
import { TonePlayer } from '../Tones/Player/TonePlayer';
import { isPlayablePlatform } from '@/utils/player/to-player-tone';
import FormatPill from '../Tones/FormatPill';
import { useToneCardTracking } from '../ToneSection/ToneSurface';

interface Props {
  children?: ReactNode;
  action?: ReactNode;
  tone: ToneCountWithUser;
  isOwner?: boolean;
  className?: string;
  titleClampClassName?: string;
  from?: string;
  isApiSelect?: boolean;
  /** Index within its row, for click-through tracking. */
  position?: number;
  /**
   * Compact card variant: hides the counts row, the time-ago, and the
   * accordion player. Used in the linked-tones section on tone detail pages
   * where the card is metadata-only.
   */
  minimal?: boolean;
  /**
   * Architecture context for the displayed model count.
   *   - Pass the partner-arch (`flow?.architecture`) in OAuth/Select contexts
   *     — the count then strictly follows that architecture per the partner
   *     contract (`visibleModelsCount`).
   *   - Leave undefined on core — the count uses the A2-preferred fallback
   *     (`preferredModelsCount`): a2_count for tones with A2, a1+custom for
   *     NAM tones without A2, `models_count` for non-NAM.
   */
  architecture?: ArchitectureFilter | null;
}

function ToneCard({
  children,
  tone,
  isOwner,
  titleClampClassName,
  from = 'search_page',
  isApiSelect = false,
  minimal = false,
  architecture = null,
  position,
}: Props) {
  const router = useRouter();
  const oauthHref = useOAuthHref();
  const { preview } = useOAuthFlow();
  const t = useTranslations();
  const href = isApiSelect
    ? oauthHref(`/api/v1/select/tones/${toTonePathname(tone)}`)
    : `/tones/${toTonePathname(tone)}`;
  const userHref = isApiSelect
    ? oauthHref(`/api/v1/select/${tone.user.username}`)
    : `/${tone.user.username}`;
  const platform = tone.platform as FORMAT;
  const gear = tone.gear as GEAR;
  const tracking = useToneCardTracking(tone.id, position, href);
  const imageUrl = tone.images && tone.images.length > 0 ? tone.images[0] : undefined;

  const publishedAt = tone.published_at || tone.created_at || undefined;
  const username = tone.user.username || '';
  const avatarUrl = tone.user.avatar_url || '';
  const displayName = tone.user.display_name;
  // Core: A2-defacto rendering via `preferredModelsCount` — a2_count for
  // tones with A2, em-dash otherwise. The em-dash is the right signal on
  // core because A2 is the new default.
  // Select / partner surfaces: strict partner-arch substitution via
  // `visibleModelsCount`. Importantly this branch also handles the
  // legacy-partner case (architecture = null/undefined) — the helper
  // returns a1+custom for NAM, so users browsing without an explicit arch
  // see the real count instead of an em-dash.
  const modelsCount = isApiSelect
    ? visibleModelsCount(tone, architecture ?? null)
    : preferredModelsCount(tone);
  const downloadsCount = tone.downloads_count || 0;
  const commentsCount = tone.visible_comments_count || 0;
  const title = tone.title || '';

  const isUserVarified = !!tone.user.is_verified;

  const handleCardClick = (e: React.MouseEvent) => {
    // Only handle clicks if they didn't originate from a link or button
    const target = e.target as HTMLElement;
    const isLinkClick = target.closest('a') || target.closest('button');

    if (!isLinkClick && href) {
      router.push(href);
    }
  };

  const renderImage = () => {
    if (imageUrl) {
      return (
        <div
          className={
            `rounded-lg flex flex-shrink-0 w-[108px] h-[108px] ${minimal ? 'sm:w-[112px] sm:h-[112px]' : 'sm:w-[140px] sm:h-[140px]'} relative overflow-hidden`
          }
        >
          <AssetImage src={imageUrl} alt={title} fill className="object-cover" sizes="140px" />
        </div>
      );
    }

    return (
      <div
        className={
          `rounded-lg flex flex-shrink-0 w-[108px] h-[108px] items-center justify-center ${minimal ? 'sm:w-[112px] sm:h-[112px]' : 'sm:w-[140px] sm:h-[140px]'}`
        }
        style={{ backgroundColor: ICON_BG_COLOR_MAP[gear] }}
      >
        <GearIcon size={56} gear={gear} isColor={true} />
      </div>
    );
  };

  const renderTitleSection = () => (
    <div>
      <h3
        className={`mb-1 text-lg sm:text-xl font-semibold overflow-hidden text-ellipsis ${titleClampClassName}`}
      >
        {title}
      </h3>
      <div className={'flex gap-4 items-center overflow-hidden'}>
        <p
          className={`text-zinc-400 text-sm sm:text-base whitespace-nowrap overflow-hidden text-ellipsis`}
        >{isApiSelect ? <><span className="md:hidden">{t(`gear.${gear}`)}</span><span className="hidden md:inline">{t(`gearCapture.${gear}`)}</span></> : GEAR_CAPTURE_MAP[gear]}</p>
        <FormatPill format={platform} />
        {/* {isOwner && (
          <>
            {tone.is_public ? (
              <Globe className={`text-zinc-400 w-4 h-4 sm:w-5 sm:h-5`} />
            ) : (
              <Lock className={`text-zinc-400 w-4 h-4 sm:w-5 sm:h-5`} />
            )}
          </>
        )} */}
      </div>
    </div>
  );

  const renderInner = () => (
    <>
      <div className="relative px-4 py-3 sm:px-6 sm:py-6">
        <div className={'flex gap-6 sm:gap-8 items-center'}>
          {href ? (
            <Link href={href} className="flex-shrink-0" aria-label="to tone">
              {renderImage()}
            </Link>
          ) : (
            renderImage()
          )}
          <div className={'flex flex-col text-left gap-2 sm:gap-3 overflow-hidden'}>
            <div className={'flex flex-col sm:gap-1/2'}>
              {href ? <Link href={href}>{renderTitleSection()}</Link> : renderTitleSection()}
            </div>
            {!minimal && (
              <div className="flex flex-row gap-6 sm:gap-8 text-zinc-400 text-sm sm:text-base">
                <div className="flex flex-row gap-1 sm:gap-2 items-center">
                  <DownloadIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                  <p>{formatCount(downloadsCount)}</p>
                </div>
                <div className="flex flex-row gap-1 sm:gap-2 items-center">
                  <FolderClosed className="w-3 h-3 sm:w-4 sm:h-4" />
                  <p>{modelsCount != null ? modelsCount.toLocaleString() : '—'}</p>
                </div>
                {commentsCount > 0 && (
                  <div className="flex flex-row gap-1 sm:gap-2 items-center">
                    <MessageCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                    <p>{formatCount(commentsCount)}</p>
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-row gap-1 items-center">
              <ToneCardUser
                username={username}
                avatarUrl={avatarUrl}
                userHref={userHref}
                displayName={displayName}
                isVerified={isUserVarified}
              />
              {isUserVarified && <Varified size={20} className="ml-2 mr-2" />}
              {!minimal && (
                <p className="text-zinc-400 text-xs sm:text-sm whitespace-nowrap overflow-hidden text-ellipsis">
                  {isUserVarified ? '' : '· '}
                  <TimeAgo date={publishedAt || ''} />
                </p>
              )}
            </div>
          </div>
        </div>
        {children && children}
      </div>
    </>
  );

  // The accordion audition player docks on core cards, and on Select cards when
  // the flow opted into previews (authorize `preview=true`). A docked player
  // gets the same treatment in both places: the card drops its bottom border +
  // bottom radius so the two merge into one box. Select preview cards therefore
  // render the core card, not the borderless `#151517` Select variant.
  const hasPlayer = !minimal && (!isApiSelect || preview);
  const wrapperBorderClasses = minimal
    ? 'border border-zinc-700 bg-[#151517]'
    : hasPlayer
      ? 'border border-b-0 rounded-b-none border-zinc-700 bg-black'
      : 'bg-[#151517]';

  return (
    <div className="relative w-full max-w-4xl m-auto flex flex-col">
      <div
        onClick={handleCardClick}
        {...tracking}
        className={`relative w-full rounded-xl ${wrapperBorderClasses} ${href ? 'cursor-pointer transition-opacity duration-100 ease-out active:opacity-70' : ''}`}
      >
        {renderInner()}
      </div>
      {hasPlayer && (
        <div className="z-20">
          <TonePlayer
            id={tone.id!.toString()}
            layout="compact"
            docked
            from={from}
            // Live mic capture/routing isn't available inside a partner plugin
            // webview, so every Select-flow player is Demo-only.
            live={!isApiSelect}
            disabled={
              !tone.has_model_with_url ||
              !isPlayablePlatform(tone.platform) ||
              (platform === FORMAT.NAM && (tone.a2_models_count ?? 0) === 0)
            }
            tones={[{ tone, lazy: 'models' }]}
          />
        </div>
      )}
    </div>
  );
}

const ToneCardWrapper = ({
  children,
  action,
  tone,
  isOwner,
  className,
  titleClampClassName = 'line-clamp-2',
  from = 'search_page',
  isApiSelect = false,
  minimal = false,
  architecture,
}: Props) => {
  return (
    <div className={`flex w-full items-center gap-4 ${className}`}>
      <ToneCard
        tone={tone}
        isOwner={isOwner}
        titleClampClassName={titleClampClassName}
        from={from}
        isApiSelect={isApiSelect}
        minimal={minimal}
        architecture={architecture}
      >
        {children}
      </ToneCard>
      {!!action && action}
    </div>
  );
};

export default ToneCardWrapper;
